import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { contractApi } from '../../services/api';
import { apiErrorCode } from '../../utils/apiError';
import { redirectToExternalSigning, signingHandoffDestination } from '../../utils/signingNavigation';

export const CUSTOMER_MESSAGE_MAX = 4000;

// Same mapping as the contract page's signing (ProjectView startExternalSigning).
const SIGN_ERROR_KEYS: Record<string, string> = {
  ROT_ALLOCATION_INVALID_DIRECTION: 'contracts.actions.signErrors.rotDirection',
  ROT_ALLOCATION_MILESTONE_TOTAL_MISMATCH: 'contracts.actions.signErrors.milestoneTotalMismatch',
  MILESTONE_AMOUNT_INVALID_DIRECTION: 'contracts.actions.signErrors.milestoneDirection',
};

/**
 * Wizard step 3: the company confirms it has reviewed the contract, then signs.
 * Signing works exactly like the contract page: `initiateSigning` creates the
 * agreement at the signing provider (Zigned) with the optional message to the
 * client. Zigned opens inside the app's signing page; its authenticated status
 * checks return the signer to the contract. Legacy providers open directly.
 */
export function useReviewStep(contractId: string | undefined) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [message, setMessageText] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState('');

  const reset = useCallback(() => {
    setMessageText('');
    setReviewed(false);
    setShowErrors(false);
    setSigning(false);
    setSignError('');
  }, []);

  /**
   * `beforeSign` persists unsaved wizard input first; it returns false when
   * something could not be saved, and signing is then not started. It returns
   * 'cancelled' when the user backed out (e.g. did not withdraw a sent
   * contract); signing then stops without an error.
   */
  const sign = useCallback(async (beforeSign?: () => Promise<boolean | 'cancelled'>) => {
    if (!reviewed) {
      setShowErrors(true);
      return;
    }
    if (!contractId || signing) return;
    setShowErrors(false);
    setSignError('');
    setSigning(true);
    try {
      const prepared = beforeSign ? await beforeSign() : true;
      if (prepared !== true) {
        if (prepared === false) setSignError(t('contracts.wizard.review.saveBeforeSignError'));
        setSigning(false);
        return;
      }
      const result = await contractApi.initiateSigning(contractId, message);
      if (!result.signingUrl) throw new Error('Missing signing URL.');
      if (result.provider === 'zigned') {
        const destination = signingHandoffDestination(contractId, result);
        navigate(destination.path, { state: destination.state });
      } else {
        redirectToExternalSigning(result.signingUrl);
      }
      return; // The app route or the external page takes over.
    } catch (error) {
      console.error('Failed to start signing from the contract wizard:', error);
      const key = SIGN_ERROR_KEYS[apiErrorCode(error) ?? ''];
      setSignError(t(key ?? 'contracts.actions.signError'));
      setSigning(false);
    }
  }, [contractId, message, reviewed, signing, t, navigate]);

  return {
    message,
    setMessage: (value: string) => setMessageText(value.slice(0, CUSTOMER_MESSAGE_MAX)),
    reviewed,
    setReviewed: (value: boolean) => {
      setReviewed(value);
      if (value) setShowErrors(false);
    },
    showErrors,
    signing,
    signError,
    sign,
    reset,
  };
}

export type ReviewStepState = ReturnType<typeof useReviewStep>;
